import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  CircularProgress, Chip, Snackbar, Alert as MuiAlert, Dialog, DialogTitle,
  DialogContent, DialogActions, Button, Radio, RadioGroup, FormControlLabel,
  Typography, Box, Menu, MenuItem,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import RepeatIcon from '@mui/icons-material/Repeat'; 
import ShareIcon from '@mui/icons-material/Share';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import QrCodeIcon from '@mui/icons-material/QrCode2';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import VerifiedIcon from '@mui/icons-material/Verified';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import { FaCalendarPlus } from 'react-icons/fa';
import { FaMapLocation } from 'react-icons/fa6';
import {
  fetchEventById, rsvpToEvent, rsvpToEventSeries, endorseEvent, fetchEventEndorsements,
  fetchEventRsvps,
  normalizeEventEndorsements,
  ENDORSEMENT_ALLOWED_ACCOUNT_TYPES,
  volunteerForEvent, optInFollowups, getEventQRCodes,
  completeFollowupStep, ignoreFollowupStep,
  fetchEventVolunteers, updateVolunteerStatus,
} from '../utils/eventApi';
import { useUser } from '../context/UserContext';
import { theme } from '../theme/colors';
import AuthRequiredModal from '../components/AlertComponents/AuthRequiredModal';
import VerifyIdentityModal from '../components/VerifyIdentityModal';
import DeleteConfirmModal from '../components/AlertComponents/DeleteConfirmModal';
import { canEditEvent, formatRecurrenceText } from '../utils/eventEditAccess';
import '../styles/component_styles/Events.scss';
import { isPoliticianSetupAccount } from '../utils/accountTypes';

/** Short format for meta block: "Mar 15, 2026, 9:00 AM - 5:00 PM" */
function formatDateRangeShort(start, end) {
  if (!start) return '';
  const s = new Date(start);
  const datePart = s.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const startTime = s.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  let str = `${datePart}, ${startTime}`;
  if (end) {
    const e = new Date(end);
    if (s.toDateString() === e.toDateString()) {
      str += ` - ${e.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
    } else {
      const endDatePart = e.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      const endTime = e.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
      str += ` - ${endDatePart}, ${endTime}`;
    }
  }
  return str;
}

const TZ_DISPLAY_NAMES = {
  'America/Los_Angeles': 'Pacific Time',
  'America/Denver': 'Mountain Time',
  'America/Phoenix': 'Arizona',
  'America/Chicago': 'Central Time',
  'America/New_York': 'Eastern Time',
  'America/Anchorage': 'Alaska Time',
  'Pacific/Honolulu': 'Hawaii Time',
};

function formatTimezoneShort(tz) {
  if (!tz) return '';
  return TZ_DISPLAY_NAMES[tz] || tz.replace(/_/g, ' ').replace(/^.*\//, '');
}

function formatLocation(ev) {
  if (ev.is_virtual) return 'Virtual Event';
  const parts = [ev.location_name, ev.address, ev.city, ev.state].filter(Boolean);
  return parts.join(', ') || 'Location TBD';
}

/** Returns { primary, secondary } for location block: e.g. "Ember Park" / "235 Tennant Avenue • Morgan Hill, CA" */
function normalizeLocationForCompare(s) {
  return (s || '')
    .toLowerCase()
    .replace(/[,.\u2022]/g, ' ')
    .replace(/\b(usa|united states)\b/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function locationsOverlap(a, b) {
  const left = normalizeLocationForCompare(a);
  const right = normalizeLocationForCompare(b);
  if (!left || !right) return false;
  if (left === right) return true;
  return left.includes(right) || right.includes(left);
}

function getLocationLines(ev) {
  if (ev.is_virtual) {
    return { primary: 'Virtual Event', secondary: ev.join_url || '' };
  }

  const addressPart = (ev.address || '').trim();
  const city = (ev.city || '').trim();
  const state = (ev.state || '').trim();
  const cityState = [city, state].filter(Boolean).join(', ');
  const composedLine = [addressPart, cityState].filter(Boolean).join(', ') || 'Location TBD';

  const locationName = (ev.location_name || '').trim();
  const venueIsDistinct =
    locationName &&
    !locationsOverlap(locationName, composedLine) &&
    !locationsOverlap(locationName, addressPart);

  if (venueIsDistinct) {
    const secondary = [addressPart, cityState].filter(Boolean).join(' • ') || '';
    return { primary: locationName, secondary };
  }

  const addressHasCity = city && addressPart.toLowerCase().includes(city.toLowerCase());
  const primary =
    addressPart && (addressHasCity || !cityState)
      ? addressPart
      : composedLine;

  return { primary: primary || 'Location TBD', secondary: '' };
}

function getEventDateRange(event) {
  const start = new Date(event.start_datetime);
  const end = event.end_datetime
    ? new Date(event.end_datetime)
    : new Date(start.getTime() + 60 * 60 * 1000);
  return { start, end };
}

function toIcsUtc(date) {
  return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
}

function escapeIcsText(text) {
  return (text || '')
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n');
}

function getCalendarLocation(event) {
  if (event.is_virtual) return event.join_url || 'Online';
  return formatLocation(event);
}

function getMapsQuery(ev) {
  const parts = [];
  if (ev.address) parts.push(ev.address);
  if (ev.location_name && !ev.address?.includes(ev.location_name)) {
    parts.push(ev.location_name);
  }
  if (ev.city) parts.push(ev.city);
  if (ev.state) parts.push(ev.state);
  return parts.join(', ');
}

function buildGoogleCalendarUrl(event) {
  const { start, end } = getEventDateRange(event);
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: event.title || '',
    dates: `${toIcsUtc(start)}/${toIcsUtc(end)}`,
    details: event.description || '',
    location: getCalendarLocation(event),
  });
  return `https://calendar.google.com/calendar/event?${params.toString()}`;
}

function buildGoogleMapsUrl(ev) {
  const query = getMapsQuery(ev);
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

function buildAppleMapsUrl(ev) {
  const query = getMapsQuery(ev);
  return `https://maps.apple.com/?q=${encodeURIComponent(query)}`;
}

function downloadEventIcs(event) {
  const { start, end } = getEventDateRange(event);
  const location = getCalendarLocation(event);
  const filenameBase = (event.title || 'event').replace(/[^\w\s-]/g, '').trim().slice(0, 50) || 'event';
  const ics = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//CivixThread//Event//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${event.id}@civixthread.com`,
    `DTSTAMP:${toIcsUtc(new Date())}`,
    `DTSTART:${toIcsUtc(start)}`,
    `DTEND:${toIcsUtc(end)}`,
    `SUMMARY:${escapeIcsText(event.title || 'Event')}`,
    `DESCRIPTION:${escapeIcsText(event.description || '')}`,
    `LOCATION:${escapeIcsText(location)}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');

  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filenameBase}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function openExternalUrl(url) {
  window.open(url, '_blank', 'noopener,noreferrer');
}

/** Green icon button that opens a small platform picker menu (calendar / maps). */
function EventMetaActionMenu({ icon, title, ariaLabel, menuItems }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  return (
    <>
      <button
        type="button"
        className="event-detail__meta-icon-btn"
        title={title}
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        {icon}
      </button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: '12px',
              minWidth: 220,
              mt: 0.5,
              border: '1px solid #e2e8f0',
              boxShadow: '0 4px 16px rgba(15, 23, 42, 0.12)',
            },
          },
        }}
      >
        {menuItems.map((item) => (
          <MenuItem
            key={item.label}
            onClick={() => {
              setAnchorEl(null);
              item.onClick();
            }}
            sx={{ fontSize: 14, fontWeight: 600, color: '#0f172a', py: 1.25 }}
          >
            {item.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

function getOrganizerTypeLabel(type) {
  if (!type) return 'Organizer';
  const t = type.toLowerCase();
  if (t === 'civixthread' || t === 'admin') return 'Hosted by CivixThread';
  if (t === 'partner') return 'Listed on Mobilize';
  if (t === 'organization' || t === 'non_profit') return 'Community Event';
  if (isPoliticianSetupAccount(t)) return 'Politician';
  if (t === 'municipality') return 'Municipality';
  return '';
}

function getInitials(name) {
  if (!name) return '';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
}

/** Resolve profile image URL (absolute or API-relative). */
function resolveProfileImageUrl(raw) {
  if (!raw || typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
  const base = process.env.REACT_APP_API_BASE_URL || '';
  if (base && trimmed.startsWith('/')) return base.replace(/\/$/, '') + trimmed;
  return trimmed;
}

/** Resolve organizer profile image URL from event (API may use organizer_profile_image or nested organizer.profile_image). */
function getOrganizerProfileImageUrl(event) {
  const raw = event?.organizer_profile_image
    || event?.organizer_profile_image_url
    || (event?.organizer && event.organizer.profile_image);
  return resolveProfileImageUrl(raw);
}

export default function EventDetailPage() {
  const muiTheme = useTheme();
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user, userId, verifiedUser, accountTypeName } = useUser();
  const isLoggedIn = !!user;

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isGoing, setIsGoing] = useState(false);
  const [isInterested, setIsInterested] = useState(false);
  const [isSeriesGoing, setIsSeriesGoing] = useState(false);
  const [rsvping, setRsvping] = useState(false);
  const [isEndorsed, setIsEndorsed] = useState(false);
  const [endorsing, setEndorsing] = useState(false);
  const [endorsers, setEndorsers] = useState([]);
  const [volunteering, setVolunteering] = useState(false);
  const [optingIn, setOptingIn] = useState(false);
  const [endorsersExpanded, setEndorsersExpanded] = useState(false);

  const [showScopeModal, setShowScopeModal] = useState(false);
  const [scopeValue, setScopeValue] = useState('this_only');

  const [showQRModal, setShowQRModal] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [loadingQR, setLoadingQR] = useState(false);
  const [qrTab, setQrTab] = useState('attendee');

  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const showToast = (message, severity = 'success') => setToast({ open: true, message, severity });
  const [showAuthRequiredModal, setShowAuthRequiredModal] = useState(false);
  const [showVerifyIdentityModal, setShowVerifyIdentityModal] = useState(false);
  const [showVolunteerConfirmDialog, setShowVolunteerConfirmDialog] = useState(false);
  const [showIgnoreFollowupModal, setShowIgnoreFollowupModal] = useState(false);
  const [ignoreFollowupStepId, setIgnoreFollowupStepId] = useState(null);
  const [ignoringFollowup, setIgnoringFollowup] = useState(false);
  const [organizerImageError, setOrganizerImageError] = useState(false);
  const [showAttendeesModal, setShowAttendeesModal] = useState(false);
  const [attendeesModalStatus, setAttendeesModalStatus] = useState('going');
  const [attendees, setAttendees] = useState([]);
  const [loadingAttendees, setLoadingAttendees] = useState(false);

  const isOrganizer = useMemo(() => {
    if (!userId || !event?.organizer_id) return false;
    return String(userId) === String(event.organizer_id);
  }, [userId, event?.organizer_id]);

  const isUserVerified = verifiedUser || isOrganizer;
  const canEndorse = isLoggedIn && !isOrganizer && accountTypeName
    && ENDORSEMENT_ALLOWED_ACCOUNT_TYPES.includes(accountTypeName);
  const isRecurring = event?.is_recurring && event?.series_id;

  const hasEventEnded = useMemo(() => {
    if (!event?.end_datetime) return false;
    return new Date(event.end_datetime) < new Date();
  }, [event?.end_datetime]);

  const followupStepsCount = event?.followup_steps_count ?? event?.followup_steps?.length ?? 0;
  const canSeeFollowups = !!event?.user_can_see_followups;
  const showFollowupOptInCta = followupStepsCount > 0 && !!event?.allow_opt_in_followups && !canSeeFollowups;
  const showFollowupSteps = (event?.followup_steps?.length ?? 0) > 0 && canSeeFollowups && hasEventEnded;

  // Update RSVP state from event data
  const updateRsvpState = useCallback((ev) => {
    if (ev?.user_rsvp) {
      setIsGoing(ev.user_rsvp.status === 'going');
      setIsInterested(ev.user_rsvp.status === 'interested');
    } else {
      setIsGoing(false);
      setIsInterested(false);
    }
    if (ev?.user_series_rsvp) {
      setIsSeriesGoing(ev.user_series_rsvp.status === 'going');
      if (!ev.user_rsvp && ev.user_series_rsvp.status === 'going') setIsGoing(true);
    } else {
      setIsSeriesGoing(false);
    }
    if (ev?.user_endorsement !== undefined) setIsEndorsed(ev.user_endorsement);
  }, []);

  const loadEndorsers = useCallback(async (id = eventId) => {
    if (!id) return [];
    try {
      const list = await fetchEventEndorsements(id);
      setEndorsers(list);
      if (list.length > 0) setEndorsersExpanded(true);
      return list;
    } catch {
      return [];
    }
  }, [eventId]);

  const loadEvent = useCallback(async () => {
    if (!eventId) return;
    setError(null);
    setLoading(true);
    try {
      const data = await fetchEventById(eventId);
      setEvent(data);
      updateRsvpState(data);
      const embedded = normalizeEventEndorsements(data?.endorsements);
      if (embedded.length > 0) {
        setEndorsers(embedded);
        setEndorsersExpanded(true);
      } else {
        await loadEndorsers(eventId);
      }
    } catch (err) {
      setError(err.message || 'Failed to load event');
    } finally {
      setLoading(false);
    }
  }, [eventId, updateRsvpState, loadEndorsers]);

  useEffect(() => { loadEvent(); }, [loadEvent]);

  // Keep endorsers in sync if event detail reports a count but list is empty.
  useEffect(() => {
    if (!eventId || loading) return;
    const count = event?.endorsement_count ?? 0;
    if (count > 0 && endorsers.length === 0) {
      loadEndorsers(eventId);
    }
  }, [eventId, event?.endorsement_count, endorsers.length, loading, loadEndorsers]);

  useEffect(() => { setOrganizerImageError(false); }, [event?.id]);
  useEffect(() => { setQrData(null); setShowQRModal(false); }, [eventId]);

  // ── RSVP ──
  const handleGoing = async () => {
    if (!isLoggedIn) { setShowAuthRequiredModal(true); return; }
    if (isRecurring) { setShowScopeModal(true); return; }
    await performRSVP('this_only');
  };

  const handleInterested = async () => {
    if (!isLoggedIn) { setShowAuthRequiredModal(true); return; }
    setRsvping(true);
    try {
      await rsvpToEvent(event.id, isInterested ? null : 'interested');
      const updated = await fetchEventById(eventId);
      setEvent(updated);
      updateRsvpState(updated);
      showToast(isInterested ? 'Interest removed.' : "You're interested!");
    } catch {
      showToast('Failed to update RSVP.', 'error');
    } finally {
      setRsvping(false);
    }
  };

  const performRSVP = async (scope) => {
    setRsvping(true);
    try {
      const wasGoing = isGoing || isSeriesGoing;
      if (scope === 'entire_series' && event.series_id) {
        await rsvpToEventSeries(event.series_id, wasGoing ? null : 'going');
      } else {
        await rsvpToEvent(event.id, isGoing ? null : 'going');
      }
      const updated = await fetchEventById(eventId);
      setEvent(updated);
      updateRsvpState(updated);
      showToast(wasGoing ? 'RSVP removed.' : "You're going!");
    } catch {
      showToast('Failed to update RSVP.', 'error');
    } finally {
      setRsvping(false);
    }
  };

  const openAttendeeList = async (status) => {
    setAttendeesModalStatus(status);
    setShowAttendeesModal(true);
    setLoadingAttendees(true);
    setAttendees([]);
    try {
      const data = await fetchEventRsvps(event.id, status);
      setAttendees(Array.isArray(data) ? data : []);
    } catch {
      showToast('Failed to load attendees.', 'error');
    } finally {
      setLoadingAttendees(false);
    }
  };

  const handleGoingPress = () => {
    if (isOrganizer) {
      openAttendeeList('going');
      return;
    }
    handleGoing();
  };

  const handleInterestedPress = () => {
    if (isOrganizer) {
      openAttendeeList('interested');
      return;
    }
    handleInterested();
  };

  // ── Endorse ──
  const handleEndorse = async () => {
    if (!isLoggedIn) { setShowAuthRequiredModal(true); return; }
    if (!isUserVerified) { setShowVerifyIdentityModal(true); return; }
    setEndorsing(true);
    try {
      await endorseEvent(event.id, !isEndorsed);
      setIsEndorsed(!isEndorsed);
      await loadEndorsers(eventId);
      setEndorsersExpanded(true);
      const updated = await fetchEventById(eventId);
      setEvent(updated);
      updateRsvpState(updated);
      showToast(isEndorsed ? 'Endorsement removed.' : 'Event endorsed!');
    } catch (err) {
      showToast(err?.message || 'Failed to endorse event.', 'error');
    } finally {
      setEndorsing(false);
    }
  };

  // ── Volunteer (matches mobile: confirm dialog, then POST, handle "Already signed up") ──
  const handleVolunteerClick = () => {
    if (!isLoggedIn) { setShowAuthRequiredModal(true); return; }
    setShowVolunteerConfirmDialog(true);
  };

  const handleVolunteerConfirm = async () => {
    setShowVolunteerConfirmDialog(false);
    setVolunteering(true);
    try {
      await volunteerForEvent(event.id);
      showToast("You've signed up to volunteer! The organizer has been notified and will confirm your spot.");
      loadEvent();
    } catch (err) {
      const msg = err?.message || '';
      if (msg.toLowerCase().includes('already signed up')) {
        showToast("You've already signed up to volunteer for this event.", 'info');
      } else {
        showToast(msg || 'Failed to sign up. Please try again.', 'error');
      }
    } finally {
      setVolunteering(false);
    }
  };

  // ── Volunteer management (organizer) ──
  const [volunteerList, setVolunteerList] = useState([]);
  const [volunteerListLoading, setVolunteerListLoading] = useState(false);
  const [showVolunteerManageModal, setShowVolunteerManageModal] = useState(false);
  const [updatingVolunteerId, setUpdatingVolunteerId] = useState(null);

  const handleOpenVolunteerManage = async () => {
    setShowVolunteerManageModal(true);
    setVolunteerListLoading(true);
    try {
      const data = await fetchEventVolunteers(event.id);
      setVolunteerList(Array.isArray(data) ? data : []);
    } catch {
      showToast('Failed to load volunteers.', 'error');
    } finally {
      setVolunteerListLoading(false);
    }
  };

  const handleUpdateVolunteerStatus = async (userId, newStatus) => {
    setUpdatingVolunteerId(userId);
    try {
      await updateVolunteerStatus(event.id, userId, newStatus);
      setVolunteerList(prev =>
        prev.map(v => (String(v.user_id || v.user?.id) === String(userId) ? { ...v, status: newStatus } : v))
      );
      showToast(`Volunteer ${newStatus === 'confirmed' ? 'approved' : 'declined'}.`);
      loadEvent();
    } catch {
      showToast('Failed to update volunteer status.', 'error');
    } finally {
      setUpdatingVolunteerId(null);
    }
  };

  // ── Follow-ups ──
  const handleOptInFollowups = async () => {
    if (!isLoggedIn) { setShowAuthRequiredModal(true); return; }
    setOptingIn(true);
    try {
      await optInFollowups(event.id);
      showToast("You've successfully opted in. See on dashboard after event.");
      loadEvent();
    } catch (err) {
      showToast(err?.message || 'Failed to opt in to follow-up steps.', 'error');
    } finally {
      setOptingIn(false);
    }
  };

  const handleCompleteStep = async (stepId) => {
    try {
      await completeFollowupStep(stepId);
      showToast('Step completed!');
      loadEvent();
    } catch {
      showToast('Failed to mark step as complete.', 'error');
    }
  };

  const handleIgnoreStepClick = (stepId) => {
    setIgnoreFollowupStepId(stepId);
    setShowIgnoreFollowupModal(true);
  };

  const handleIgnoreStepConfirm = async () => {
    if (!ignoreFollowupStepId) return;
    setIgnoringFollowup(true);
    try {
      await ignoreFollowupStep(ignoreFollowupStepId);
      setShowIgnoreFollowupModal(false);
      setIgnoreFollowupStepId(null);
      loadEvent();
    } catch {
      showToast('Failed to ignore step.', 'error');
    } finally {
      setIgnoringFollowup(false);
    }
  };

  // ── QR ──
  const handleOpenQR = async () => {
    if (!isOrganizer) return;
    setShowQRModal(true);
    if (!qrData) {
      setLoadingQR(true);
      try {
        const data = await getEventQRCodes(event.id);
        setQrData(data);
      } catch {
        showToast('Failed to load QR codes.', 'error');
      } finally {
        setLoadingQR(false);
      }
    }
  };

  // ── Share ──
  const handleShare = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({ title: event.title, url });
    } else {
      navigator.clipboard.writeText(url);
      showToast('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="event-detail-page-wrapper" style={{ minHeight: '100vh', backgroundColor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={40} sx={{ color: theme.primary }} />
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="event-detail-page-wrapper" style={{ minHeight: '100vh', backgroundColor: '#f8fafc', padding: 40, textAlign: 'center' }}>
        <div className="events-page__error">
          <p>{error || 'Event not found.'}</p>
          <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', mt: 2 }}>
            <Button variant="outlined" onClick={() => navigate('/events')}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', borderColor: '#0f172a', color: '#0f172a' }}>
              Back to Events
            </Button>
            <Button variant="contained" onClick={loadEvent}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', background: theme.gradient }}>
              Try Again
            </Button>
          </Box>
        </div>
      </div>
    );
  }

  const attendeeCount = event.attendee_count || event.going_count || 0;
  const interestedCount = event.interested_count || 0;
  const endorsementCount = Math.max(endorsers.length, event.endorsement_count ?? 0);
  const showEndorsementsSection = endorsementCount > 0 || canEndorse || isOrganizer;

  return (
    <div className="event-detail-page-wrapper" style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      <div className="event-detail">
        {(event.cover_image || event.cover_image_url) && (
          <img src={event.cover_image || event.cover_image_url} alt="" className="event-detail__cover" />
        )}

      {/* Badges row — event type + recurring + status */}
      <div className="event-detail__badges">
        {(event.event_type?.display_name || event.event_type?.name || event.event_type_name) && (
          <Chip label={event.event_type?.display_name || event.event_type?.name || event.event_type_name} size="small" sx={{ backgroundColor: `${theme.primary}12`, color: theme.primary, fontWeight: 600 }} />
        )}
        {isRecurring && (
          <Chip icon={<RepeatIcon sx={{ fontSize: 14 }} />} label={formatRecurrenceText(event)} size="small" variant="outlined" />
        )}
        {isOrganizer ? (
          <Chip label="Hosting" size="small" sx={{ backgroundColor: theme.primary, color: '#fff', fontWeight: 600 }} />
        ) : (
          <>
            {isGoing && <Chip label="Going" size="small" sx={{ backgroundColor: theme.primary, color: '#fff', fontWeight: 600 }} />}
            {isInterested && !isGoing && <Chip label="Interested" size="small" variant="outlined" sx={{ borderColor: theme.primary, color: theme.primary }} />}
          </>
        )}
      </div>

      <h1 className="event-detail__title">{event.title}</h1>

      {/* Organizer — clickable, with type badge; show profile image when API provides one */}
      {event.organizer_name && (() => {
        const organizerImageUrl = getOrganizerProfileImageUrl(event);
        const showOrganizerImage = organizerImageUrl && !organizerImageError;
        return (
        <Link
          to={`/profile/${event.organizer_id}`}
          className="event-detail__organizer"
          style={{ textDecoration: 'none', color: 'inherit' }}
        >
          {showOrganizerImage ? (
            <img
              src={organizerImageUrl}
              alt=""
              className="event-detail__organizer-avatar"
              onError={() => setOrganizerImageError(true)}
            />
          ) : (
            <div className="event-detail__organizer-avatar-placeholder">
              {getInitials(event.organizer_name)}
            </div>
          )}
          <div>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, maxWidth: '100%' }}>
              <Typography variant="body2" fontWeight={600} sx={{ color: '#0f172a' }}>{event.organizer_name}</Typography>
              {event.organizer_is_verified ? (
                <VerifiedIcon
                  sx={{ fontSize: 18, color: '#8B6914', flexShrink: 0 }}
                  aria-label="Verified"
                  titleAccess="Verified"
                />
              ) : null}
            </Box>
            <Typography variant="caption" sx={{ color: '#64748b' }}>{getOrganizerTypeLabel(event.organizer_type)}</Typography>
          </div>
        </Link>
        );
      })()}

      {/* Meta info — date/time and location blocks (mobile-style) */}
      <div className="event-detail__meta">
        <div className="event-detail__meta-block">
          <div className="event-detail__meta-text">
            <div className="event-detail__meta-primary">
              {formatDateRangeShort(event.start_datetime, event.end_datetime)}
            </div>
            {event.timezone && (
              <div className="event-detail__meta-secondary">
                {formatTimezoneShort(event.timezone)}
              </div>
            )}
          </div>
          <EventMetaActionMenu
            icon={<FaCalendarPlus style={{ fontSize: 22 }} />}
            title="Add to calendar"
            ariaLabel="Add to calendar"
            menuItems={[
              {
                label: 'Google Calendar',
                onClick: () => openExternalUrl(buildGoogleCalendarUrl(event)),
              },
              {
                label: 'Download calendar file (.ics)',
                onClick: () => downloadEventIcs(event),
              },
            ]}
          />
        </div>

        <div className="event-detail__meta-block">
          <div className="event-detail__meta-text">
            {(() => {
              const { primary, secondary } = getLocationLines(event);
              return (
                <>
                  <div className="event-detail__meta-primary">{primary}</div>
                  {secondary && (
                    <div className="event-detail__meta-secondary">{secondary}</div>
                  )}
                </>
              );
            })()}
          </div>
          {event.is_virtual && event.join_url ? (
            <a
              href={event.join_url}
              target="_blank"
              rel="noopener noreferrer"
              className="event-detail__meta-icon-btn"
              title="Join online"
            >
              <OpenInNewIcon sx={{ fontSize: 22 }} />
            </a>
          ) : !event.is_virtual && getMapsQuery(event) && (
            <EventMetaActionMenu
              icon={<FaMapLocation style={{ fontSize: 22 }} />}
              title="Open in maps"
              ariaLabel="Open in maps"
              menuItems={[
                {
                  label: 'Google Maps',
                  onClick: () => openExternalUrl(buildGoogleMapsUrl(event)),
                },
                {
                  label: 'Apple Maps',
                  onClick: () => openExternalUrl(buildAppleMapsUrl(event)),
                },
              ]}
            />
          )}
        </div>
      </div>

      {/* RSVP — flat toggle buttons with inline counts */}
      <div className="event-detail__rsvp-block">
        <div className="event-detail__rsvp-cards" role="group" aria-label="RSVP options">
          <div className={`event-detail__rsvp-card ${(isGoing || isOrganizer) ? 'event-detail__rsvp-card--active' : ''}`}>
            <button
              type="button"
              className="event-detail__rsvp-card-main"
              onClick={handleGoingPress}
              disabled={rsvping}
              aria-pressed={isGoing || isOrganizer}
              aria-label={`RSVP as going. ${attendeeCount} ${attendeeCount === 1 ? 'person' : 'people'} going.`}
            >
              Going ({attendeeCount})
            </button>
            {(attendeeCount > 0 || isOrganizer) && (
              <button
                type="button"
                className="event-detail__rsvp-card-list"
                onClick={() => openAttendeeList('going')}
                aria-label="See who is going"
              >
                <ChevronRightIcon sx={{ fontSize: 20 }} />
              </button>
            )}
          </div>
          <div className={`event-detail__rsvp-card event-detail__rsvp-card--interested ${isInterested ? 'event-detail__rsvp-card--active' : ''}`}>
            <button
              type="button"
              className="event-detail__rsvp-card-main"
              onClick={handleInterestedPress}
              disabled={rsvping || isOrganizer}
              aria-pressed={isInterested}
              aria-label={`RSVP as interested. ${interestedCount} ${interestedCount === 1 ? 'person' : 'people'} interested.`}
            >
              Interested ({interestedCount})
            </button>
            {(interestedCount > 0 || isOrganizer) && (
              <button
                type="button"
                className="event-detail__rsvp-card-list"
                onClick={() => openAttendeeList('interested')}
                aria-label="See who is interested"
              >
                <ChevronRightIcon sx={{ fontSize: 20 }} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Friends Going */}
      {isLoggedIn && event.friends_attending?.length > 0 && (
        <div className="event-detail__section">
          <h3 className="event-detail__section-title">Friends Going</h3>
          <div className="event-detail__friends-row">
            {event.friends_attending.map((friend) => (
              <div key={friend.id || friend.user_id} className="event-detail__friend-item">
                {friend.profile_image ? (
                  <img src={friend.profile_image} alt="" className="event-detail__friend-avatar" />
                ) : (
                  <div className="event-detail__friend-avatar-placeholder">
                    {getInitials(friend.first_name || friend.name || '')}
                  </div>
                )}
                <span className="event-detail__friend-name">{friend.first_name || friend.name?.split(' ')[0] || ''}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Endorsements section — visible to everyone once there are endorsers, or to eligible endorsers, or always to organizer */}
      {showEndorsementsSection && (
        <div className="event-detail__section event-detail__endorsements-section">
          <button
            type="button"
            onClick={() => setEndorsersExpanded(x => !x)}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              width: '100%', background: 'none', border: 'none', padding: 0,
              cursor: 'pointer', textAlign: 'left',
            }}
            aria-expanded={endorsersExpanded}
          >
            <h3 className="event-detail__section-title" style={{ margin: 0 }}>
              Endorsements{endorsementCount > 0 ? ` · ${endorsementCount}` : ''}
            </h3>
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                background: theme.gradient,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <ExpandMoreIcon
                sx={{
                  fontSize: 18,
                  color: '#fff',
                  transition: 'transform 0.2s',
                  transform: endorsersExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                }}
              />
            </Box>
          </button>

          {endorsersExpanded && (
            <Box sx={{ mt: 1.5 }}>
              {endorsers.length === 0 ? (
                <Typography variant="body2" sx={{ color: '#94a3b8', fontStyle: 'italic' }}>
                  No endorsements yet.
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                  {endorsers.map((e) => (
                    <Link
                      key={e.id}
                      to={`/profile/${e.user_id}`}
                      style={{ textDecoration: 'none', color: 'inherit' }}
                    >
                      <Box
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 1.5,
                          py: 1.25, px: 0.5, borderRadius: '10px',
                          '&:hover': { bgcolor: '#f8fafc' },
                          transition: 'background 0.15s',
                        }}
                      >
                        {e.user_profile_image ? (
                          <Box
                            component="img"
                            src={e.user_profile_image}
                            alt=""
                            sx={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
                              bgcolor: `${theme.primary}20`, color: theme.primary,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontWeight: 700, fontSize: 15,
                            }}
                          >
                            {(e.user_name || '?').charAt(0).toUpperCase()}
                          </Box>
                        )}
                        <Box>
                          <Typography variant="body2" fontWeight={600} sx={{ color: '#0f172a', lineHeight: 1.3 }}>
                            {e.user_name}
                          </Typography>
                          {e.user_account_type && (
                            <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'capitalize' }}>
                              {e.user_account_type.replace(/_/g, ' ')}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Link>
                  ))}
                </Box>
              )}

              {canEndorse && (
                <button
                  type="button"
                  className={`event-detail__action-btn event-detail__endorse-btn${isEndorsed ? ' event-detail__action-btn--endorsed' : ''}`}
                  onClick={handleEndorse}
                  disabled={endorsing}
                  style={{ marginTop: 12 }}
                  aria-pressed={isEndorsed}
                >
                  {endorsing ? (
                    <CircularProgress size={16} sx={{ color: isEndorsed ? '#fff' : theme.primary }} />
                  ) : isEndorsed ? (
                    <>
                      <CheckCircleIcon sx={{ fontSize: 18 }} />
                      Endorsed
                    </>
                  ) : (
                    'Endorse this event'
                  )}
                </button>
              )}
            </Box>
          )}
        </div>
      )}

      {/* Virtual link (standalone section kept for visibility) */}
      {event.is_virtual && event.join_url && (
        <div className="event-detail__section">
          <h3 className="event-detail__section-title">Join Online</h3>
          <a
            href={event.join_url}
            target="_blank"
            rel="noopener noreferrer"
            className="event-detail__join-link"
          >
            {event.join_url}
          </a>
        </div>
      )}

      {/* Description */}
      {event.description && (
        <div className="event-detail__section">
          <h3 className="event-detail__section-title">About this Event</h3>
          <p className="event-detail__description">{event.description}</p>
        </div>
      )}

      {/* MVP: Event tags commented out for now until they are used in discovery/filtering. */}

      {/* Volunteers (UI/UX and backend aligned with mobile) */}
      {event.needs_volunteers && (() => {
        const isPastEvent = !canEditEvent(event);
        const isFull = event.volunteers_needed_count > 0 && (event.volunteer_count ?? 0) >= event.volunteers_needed_count;
        return (
          <div className="event-detail__section">
            <div className="event-detail__volunteer-cta">
              <div className="event-detail__volunteer-cta-top">
                <div className="event-detail__volunteer-cta-header">
                  <Typography variant="subtitle1" fontWeight={600} className="event-detail__volunteer-cta-title">
                    Volunteers Needed
                    {event.volunteers_needed_count != null && (
                      <span className="event-detail__volunteer-cta-subtitle">
                        {' '}({event.volunteer_count ?? 0} / {event.volunteers_needed_count} spots filled)
                      </span>
                    )}
                  </Typography>
                </div>
                {isLoggedIn && !isOrganizer && (() => {
                  const volunteerStatus = event.user_volunteer?.status;
                  if (volunteerStatus) {
                    const isConfirmed = volunteerStatus === 'confirmed' || volunteerStatus === 'approved';
                    const isDeclined = volunteerStatus === 'declined';
                    const statusColor = isConfirmed
                      ? theme.primary
                      : isDeclined
                        ? '#b91c1c'
                        : '#0f172a';
                    const statusMessage = isConfirmed
                      ? "You're confirmed as a volunteer!"
                      : isDeclined
                        ? 'Your volunteer request was declined.'
                        : "You've signed up — waiting for confirmation.";
                    const StatusIcon = isConfirmed ? CheckCircleOutlineIcon : isDeclined ? CancelOutlinedIcon : ScheduleIcon;
                    return (
                      <div className="event-detail__volunteer-status-row" style={{ borderColor: statusColor }}>
                        <StatusIcon sx={{ fontSize: 20, color: statusColor }} />
                        <Typography variant="body2" fontWeight={500} style={{ color: statusColor }}>
                          {statusMessage}
                        </Typography>
                      </div>
                    );
                  }
                  if (isPastEvent) {
                    return (
                      <Typography variant="body2" sx={{ color: '#64748b', fontStyle: 'italic' }}>
                        Volunteer sign-up is closed for past events.
                      </Typography>
                    );
                  }
                  if (isFull) {
                    return (
                      <Typography variant="body2" sx={{ color: '#64748b', fontStyle: 'italic' }}>
                        All volunteer spots have been filled.
                      </Typography>
                    );
                  }
                  return (
                    <button
                      type="button"
                      className="event-detail__volunteer-cta-btn"
                      onClick={handleVolunteerClick}
                      disabled={volunteering}
                    >
                      {volunteering ? (
                        <CircularProgress size={20} sx={{ color: '#fff' }} />
                      ) : (
                        <>
                          <VolunteerActivismIcon sx={{ fontSize: 18 }} />
                          <span>Volunteer</span>
                        </>
                      )}
                    </button>
                  );
                })()}
              </div>
              {event.volunteer_description && (
                <Typography variant="body2" className="event-detail__volunteer-cta-description">
                  {event.volunteer_description}
                </Typography>
              )}
            </div>
          </div>
        );
      })()}

      <DeleteConfirmModal
        open={showVolunteerConfirmDialog}
        onClose={() => setShowVolunteerConfirmDialog(false)}
        onConfirm={handleVolunteerConfirm}
        title="Volunteer"
        description="Would you like to volunteer for this event? The organizer will be notified of your interest."
        confirmLabel="Sign Up"
        loading={volunteering}
      />

      {/* Follow-up opt-in CTA (before user can see steps) */}
      {showFollowupOptInCta && (
        <div className="event-detail__section">
          <div className="event-detail__opt-in-card">
            <NotificationsOutlinedIcon sx={{ color: theme.primary, fontSize: 28, flexShrink: 0 }} />
            <div className="event-detail__opt-in-content">
              <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#0f172a', mb: 0.5 }}>
                Get Follow-Up Steps
              </Typography>
              <Typography variant="body2" sx={{ color: '#334155', lineHeight: 1.4 }}>
                {hasEventEnded
                  ? 'Opt in to see actionable steps you can take after this event.'
                  : 'Opt in to receive follow-up steps after this event ends.'}
              </Typography>
            </div>
            <button
              type="button"
              className="event-detail__opt-in-btn"
              onClick={handleOptInFollowups}
              disabled={optingIn}
            >
              {optingIn ? <CircularProgress size={18} sx={{ color: '#ffffff' }} /> : 'Opt In'}
            </button>
          </div>
        </div>
      )}

      {/* Follow-up steps (only after event ends and user can see them) */}
      {showFollowupSteps && (
        <div className="event-detail__section">
          <div className="event-detail__section-title-row">
            <FlagOutlinedIcon sx={{ color: theme.primary, fontSize: 22 }} />
            <h3 className="event-detail__section-title" style={{ margin: 0 }}>Follow-Up Actions</h3>
          </div>
          {event.followup_steps.map((step, idx) => (
            <div key={step.id || idx} className="event-detail__followup-step-card">
              <div className="event-detail__followup-step-header">
                <div className="event-detail__followup-step-badge">
                  {step.step_order || idx + 1}
                </div>
                <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#0f172a', flex: 1 }}>
                  {step.title}
                </Typography>
                {step.user_status === 'completed' && (
                  <CheckCircleIcon sx={{ color: '#16a34a', fontSize: 22, flexShrink: 0 }} />
                )}
                {step.user_status === 'ignored' && (
                  <CancelOutlinedIcon sx={{ color: '#64748b', fontSize: 22, flexShrink: 0 }} />
                )}
              </div>

              {step.description && (
                <Typography variant="body2" sx={{ color: '#334155', mt: 1, lineHeight: 1.5 }}>
                  {step.description}
                </Typography>
              )}

              {step.image_url && (
                <img
                  src={step.image_url}
                  alt=""
                  className="event-detail__followup-step-hero-image"
                />
              )}

              {(step.action_link || step.action_url) && (
                <a
                  href={step.action_link || step.action_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="event-detail__followup-action-btn"
                >
                  Take Action
                  <OpenInNewIcon sx={{ fontSize: 16 }} />
                </a>
              )}

              {!step.user_status && (
                <div className="event-detail__followup-step-actions">
                  <button
                    type="button"
                    className="event-detail__followup-complete-btn"
                    onClick={() => handleCompleteStep(step.id)}
                  >
                    <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />
                    Mark Complete
                  </button>
                  <button
                    type="button"
                    className="event-detail__followup-ignore-btn"
                    onClick={() => handleIgnoreStepClick(step.id)}
                  >
                    <CancelOutlinedIcon sx={{ fontSize: 16 }} />
                    Ignore
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <DeleteConfirmModal
        open={showIgnoreFollowupModal}
        onClose={() => {
          if (ignoringFollowup) return;
          setShowIgnoreFollowupModal(false);
          setIgnoreFollowupStepId(null);
        }}
        onConfirm={handleIgnoreStepConfirm}
        title="Ignore Step"
        description="Are you sure you want to ignore this step?"
        confirmLabel="Ignore"
        loading={ignoringFollowup}
      />

      {/* Signup prompt for non-logged-in users — after event content, before share (matches mobile) */}
      {!isLoggedIn && (
        <div className="event-detail__signup-prompt">
          <Typography variant="h6" fontWeight={700} className="event-detail__signup-prompt-title">
            Unlock the full event experience
          </Typography>
          <ul className="event-detail__signup-benefits">
            <li>
              <EmojiEventsOutlinedIcon sx={{ fontSize: 22, color: theme.primary, flexShrink: 0 }} />
              <span>Earn badges for attending events</span>
            </li>
            <li>
              <VolunteerActivismIcon sx={{ fontSize: 22, color: theme.primary, flexShrink: 0 }} />
              <span>Earn volunteer badges when you help out</span>
            </li>
            <li>
              <TrendingUpOutlinedIcon sx={{ fontSize: 22, color: theme.primary, flexShrink: 0 }} />
              <span>Track your civic engagement over time</span>
            </li>
            <li>
              <GroupsOutlinedIcon sx={{ fontSize: 22, color: theme.primary, flexShrink: 0 }} />
              <span>Connect with other attendees</span>
            </li>
          </ul>
          <button
            type="button"
            className="event-detail__signup-btn"
            onClick={() => navigate('/auth/signup')}
          >
            Sign up to join
          </button>
        </div>
      )}

      {/* Share + organizer tools — full-width toolbar at bottom */}
      <div className="event-detail__bottom-toolbar">
        <button type="button" className="event-detail__bottom-toolbar-btn" onClick={handleShare}>
          <ShareIcon sx={{ fontSize: 20 }} />
          Share
        </button>
        {isOrganizer && (
          <>
            <button type="button" className="event-detail__bottom-toolbar-btn" onClick={handleOpenQR}>
              <QrCodeIcon sx={{ fontSize: 20 }} />
              QR codes
            </button>
            {event.needs_volunteers && (
              <button type="button" className="event-detail__bottom-toolbar-btn event-detail__bottom-toolbar-btn--with-badge" onClick={handleOpenVolunteerManage}>
                <VolunteerActivismIcon sx={{ fontSize: 20 }} />
                Volunteers
                {(event.pending_volunteer_count ?? 0) > 0 && (
                  <span className="event-detail__toolbar-badge" aria-label={`${event.pending_volunteer_count} pending volunteer${event.pending_volunteer_count === 1 ? '' : 's'}`}>
                    {event.pending_volunteer_count}
                  </span>
                )}
              </button>
            )}
            {canEditEvent(event) && (
              <button
                type="button"
                className="event-detail__bottom-toolbar-btn"
                onClick={() => navigate(`/events/${event.id}/edit`)}
              >
                <EditIcon sx={{ fontSize: 20 }} />
                Edit
              </button>
            )}
          </>
        )}
      </div>

      {/* ── Auth required for guests (log in/sign up) ── */}
      <AuthRequiredModal
        open={showAuthRequiredModal}
        onClose={() => setShowAuthRequiredModal(false)}
        onLogin={() => {
          setShowAuthRequiredModal(false);
          navigate('/auth/login');
        }}
        title="You're browsing public events"
        description="Sign in to RSVP, volunteer, host events, and track events you're interested in."
        confirmLabel="Sign in"
        dismissLabel="Keep browsing"
      />

      {/* ── Verify identity (logged in but not verified) – matches mobile VerifyIdentityModal ── */}
      <VerifyIdentityModal
        isOpen={showVerifyIdentityModal}
        onClose={() => setShowVerifyIdentityModal(false)}
      />

      {/* ── RSVP Scope Modal (recurring) ── */}
      <Dialog open={showScopeModal} onClose={() => setShowScopeModal(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>RSVP Scope</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: '#0f172a' }}>
            This is a recurring event. How would you like to RSVP?
          </Typography>
          <RadioGroup value={scopeValue} onChange={(e) => setScopeValue(e.target.value)}>
            <FormControlLabel value="this_only" control={<Radio sx={{ color: theme.primary, '&.Mui-checked': { color: theme.primary } }} />} label="Just this event" />
            <FormControlLabel value="entire_series" control={<Radio sx={{ color: theme.primary, '&.Mui-checked': { color: theme.primary } }} />} label="Entire series" />
          </RadioGroup>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowScopeModal(false)}
            variant="outlined"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              color: '#0f172a',
              borderColor: '#0f172a',
              backgroundColor: '#fff',
              boxShadow: muiTheme.shadows[2],
              '&:hover': {
                borderColor: '#0f172a',
                backgroundColor: 'rgba(15, 23, 42, 0.06)',
                boxShadow: muiTheme.shadows[2],
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => { setShowScopeModal(false); performRSVP(scopeValue); }}
            variant="contained"
            sx={{
              background: theme.gradient,
              boxShadow: muiTheme.shadows[2],
              '&:hover': { background: theme.gradient, opacity: 0.92, boxShadow: muiTheme.shadows[2] },
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── QR Code Modal ── */}
      <Dialog 
        open={showQRModal} 
        onClose={() => setShowQRModal(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            p: 1,
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1.25rem', color: '#0f172a', pb: 1 }}>
          Event QR Codes
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14, color: '#64748b', mb: 3 }}>
            Attendees can scan these codes to check in to the event.
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1, mb: 4, justifyContent: 'center' }}>
            <Chip 
              label="Attendee Check-in" 
              onClick={() => setQrTab('attendee')} 
              sx={{ 
                fontWeight: 600, 
                px: 1,
                py: 2.5,
                borderRadius: '12px',
                ...(qrTab === 'attendee' 
                  ? { backgroundColor: theme.primary, color: '#fff', '&:hover': { backgroundColor: theme.primary } } 
                  : { backgroundColor: '#f1f5f9', color: '#475569', '&:hover': { backgroundColor: '#e2e8f0' } }
                ) 
              }} 
            />
            <Chip 
              label="Volunteer Check-in" 
              onClick={() => setQrTab('volunteer')} 
              sx={{ 
                fontWeight: 600, 
                px: 1,
                py: 2.5,
                borderRadius: '12px',
                ...(qrTab === 'volunteer' 
                  ? { backgroundColor: theme.primary, color: '#fff', '&:hover': { backgroundColor: theme.primary } } 
                  : { backgroundColor: '#f1f5f9', color: '#475569', '&:hover': { backgroundColor: '#e2e8f0' } }
                ) 
              }} 
            />
          </Box>
          
          {loadingQR ? (
            <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress size={32} sx={{ color: theme.primary }} /></Box>
          ) : qrData ? (
            <Box sx={{ 
              textAlign: 'center', 
              p: 3, 
              bgcolor: '#f8fafc', 
              borderRadius: '16px',
              border: '1px solid #e2e8f0',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '300px'
            }}>
              {qrTab === 'attendee' && (qrData.attendee_qr_image_data || qrData.attendee_qr_url) && (
                <img src={qrData.attendee_qr_image_data || qrData.attendee_qr_url} alt="Attendee QR" style={{ maxWidth: 240, width: '100%', borderRadius: '8px' }} />
              )}
              {qrTab === 'volunteer' && (qrData.volunteer_qr_image_data || qrData.volunteer_qr_url) && (
                <img src={qrData.volunteer_qr_image_data || qrData.volunteer_qr_url} alt="Volunteer QR" style={{ maxWidth: 240, width: '100%', borderRadius: '8px' }} />
              )}
              {((qrTab === 'attendee' && !qrData.attendee_qr_image_data && !qrData.attendee_qr_url) || (qrTab === 'volunteer' && !qrData.volunteer_qr_image_data && !qrData.volunteer_qr_url)) && (
                <Typography color="textSecondary">No QR code available for this type.</Typography>
              )}
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 6, bgcolor: '#f8fafc', borderRadius: '16px', border: '1px solid #e2e8f0' }}>
              <Typography color="textSecondary">No QR data available.</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
          <Button 
            onClick={() => setShowQRModal(false)} 
            variant="outlined"
            sx={{ 
              color: '#0f172a', 
              borderColor: '#e2e8f0',
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              '&:hover': {
                borderColor: '#cbd5e1',
                backgroundColor: '#f8fafc'
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Volunteer Management Modal (organizer only) ── */}
      <Dialog
        open={showVolunteerManageModal}
        onClose={() => setShowVolunteerManageModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1.25rem', color: '#0f172a', pb: 0 }}>
          Manage Volunteers
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14, color: '#64748b', mb: 2 }}>
            Approve or decline volunteer sign-ups for this event.
          </Typography>
          {volunteerListLoading ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <CircularProgress size={28} sx={{ color: theme.primary }} />
            </Box>
          ) : volunteerList.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 5, bgcolor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
              <Typography color="textSecondary">No volunteer sign-ups yet.</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {volunteerList.map((vol) => {
                const uid = vol.user_id || vol.user?.id;
                const name = [vol.user?.first_name, vol.user?.last_name].filter(Boolean).join(' ') || vol.user?.username || 'Volunteer';
                const profileImageUrl = resolveProfileImageUrl(vol.user?.profile_image);
                const isUpdating = String(updatingVolunteerId) === String(uid);
                const statusLabel = vol.status === 'confirmed' ? 'Approved'
                  : vol.status === 'declined' ? 'Declined'
                  : vol.status === 'completed' ? 'Completed'
                  : 'Pending';
                const statusColor = vol.status === 'confirmed' ? '#16a34a'
                  : vol.status === 'declined' ? '#b91c1c'
                  : vol.status === 'completed' ? '#0284c7'
                  : '#64748b';
                return (
                  <Box key={uid} sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5,
                    p: 1.5, borderRadius: '12px', border: '1px solid #e2e8f0', bgcolor: '#fff',
                  }}>
                    <Box
                      component={Link}
                      to={`/profile/${uid}`}
                      onClick={() => setShowVolunteerManageModal(false)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        minWidth: 0,
                        flex: 1,
                        textDecoration: 'none',
                        color: 'inherit',
                        '&:hover .event-detail__volunteer-name': { textDecoration: 'underline' },
                      }}
                    >
                      {profileImageUrl ? (
                        <Box
                          component="img"
                          src={profileImageUrl}
                          alt=""
                          sx={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                        />
                      ) : (
                        <Box sx={{
                          width: 44, height: 44, borderRadius: '50%', bgcolor: '#ecfdf5',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: theme.primary, fontWeight: 700, fontSize: 15, flexShrink: 0,
                        }}>
                          {getInitials(name)}
                        </Box>
                      )}
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          className="event-detail__volunteer-name"
                          sx={{ color: '#0f172a' }}
                          noWrap
                        >
                          {name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: statusColor, fontWeight: 600 }}>
                          {statusLabel}
                        </Typography>
                      </Box>
                    </Box>
                    {(vol.status === 'pending') && (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          size="small" variant="contained"
                          disabled={isUpdating}
                          onClick={() => handleUpdateVolunteerStatus(uid, 'confirmed')}
                          sx={{
                            background: theme.gradient, textTransform: 'none', fontWeight: 600,
                            borderRadius: '8px', fontSize: 12, minWidth: 72,
                            '&:hover': { background: theme.gradient, opacity: 0.9 },
                          }}
                        >
                          {isUpdating ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Approve'}
                        </Button>
                        <Button
                          size="small" variant="outlined"
                          disabled={isUpdating}
                          onClick={() => handleUpdateVolunteerStatus(uid, 'declined')}
                          sx={{
                            textTransform: 'none', fontWeight: 600, borderRadius: '8px', fontSize: 12,
                            color: '#b91c1c', borderColor: '#fecaca', minWidth: 72,
                            '&:hover': { borderColor: '#f87171', bgcolor: '#fef2f2' },
                          }}
                        >
                          Decline
                        </Button>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
          <Button
            onClick={() => setShowVolunteerManageModal(false)}
            variant="outlined"
            sx={{
              color: '#0f172a', borderColor: '#e2e8f0', borderRadius: '10px',
              textTransform: 'none', fontWeight: 600, px: 3,
              '&:hover': { borderColor: '#cbd5e1', backgroundColor: '#f8fafc' },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Attendees Modal ── */}
      <Dialog
        open={showAttendeesModal}
        onClose={() => setShowAttendeesModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px' } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1.25rem', color: '#0f172a', pb: 1 }}>
          {attendeesModalStatus === 'going' ? 'Going' : 'Interested'}
        </DialogTitle>
        <DialogContent sx={{ px: 0, pt: 0 }}>
          {loadingAttendees ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <CircularProgress size={28} sx={{ color: theme.primary }} />
            </Box>
          ) : attendees.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 5, px: 3 }}>
              <Typography color="textSecondary">No one yet</Typography>
            </Box>
          ) : (
            <Box>
              {attendees.map((attendee) => {
                const accountLabel = (attendee.user_account_type || '').replace(/_/g, ' ');
                return (
                  <Box
                    key={attendee.id}
                    component={Link}
                    to={`/profile/${attendee.user_id}`}
                    onClick={() => setShowAttendeesModal(false)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      px: 3,
                      py: 1.5,
                      textDecoration: 'none',
                      color: 'inherit',
                      borderBottom: '1px solid #f1f5f9',
                      '&:hover': { bgcolor: '#f8fafc' },
                    }}
                  >
                    {attendee.user_profile_image ? (
                      <Box component="img" src={attendee.user_profile_image} alt="" sx={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover' }} />
                    ) : (
                      <Box sx={{
                        width: 44, height: 44, borderRadius: '50%', bgcolor: '#ecfdf5',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: theme.primary, fontWeight: 700, fontSize: 15,
                      }}>
                        {getInitials(attendee.user_name)}
                      </Box>
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} sx={{ color: '#0f172a' }} noWrap>
                        {attendee.user_name}
                      </Typography>
                      {accountLabel && (
                        <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'capitalize' }} noWrap>
                          {accountLabel}
                        </Typography>
                      )}
                    </Box>
                    <ChevronRightIcon sx={{ fontSize: 20, color: '#94a3b8' }} />
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setShowAttendeesModal(false)} sx={{ textTransform: 'none', fontWeight: 600 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <Snackbar open={toast.open} autoHideDuration={4000} onClose={() => setToast(t => ({ ...t, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <MuiAlert onClose={() => setToast(t => ({ ...t, open: false }))} severity={toast.severity} variant="filled" sx={{ borderRadius: 2 }}>
          {toast.message}
        </MuiAlert>
      </Snackbar>
    </div>
    </div>
  );
}
